"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { stageInBatches } from "@/lib/stage";
import { useFetch } from "@/lib/use-fetch";
import { Alert, Badge, Button, Card, Input, Modal, PageHeader, Textarea } from "@/components/ui";
import { SenderSelect } from "@/components/sender-select";
import { GroupPicker, TemplatePicker } from "@/components/pickers";
import { useToast } from "@/components/toast";
import { ConfirmRow, MessagePreview, SenderHeader } from "@/components/confirm-sms";
import { estimateSegments, parseNumbers } from "@/lib/segments";
import { buildRateLookup, categorize } from "@/lib/sms-rates";
import { datetimeLocalMin } from "@/lib/datetime";
import { notifyAccountChanged } from "@/lib/account";
import type { Account, BulkResult, Contact, ContactGroup, ContactSegment, SmsRate } from "@/lib/types";

const CAT_TONE: Record<string, "green" | "blue" | "amber"> = { LOCAL: "green", NATIONAL: "blue", INTERNATIONAL: "amber" };
const CAT_LABEL: Record<string, string> = { LOCAL: "Local", NATIONAL: "National", INTERNATIONAL: "International" };
const CAT_ORDER = ["LOCAL", "NATIONAL", "INTERNATIONAL"];

// Recipients are uploaded/sent in batches so a single huge list (up to ~1M) never
// becomes one oversized HTTP request. Each batch must stay within the backend
// BULK_MAX_DESTINATIONS cap (default 10000). Smaller batches hold each enqueue
// transaction's locks for less time (less contention with the dispatch worker) and
// let the gateway interleave other requests — so the portal stays responsive and
// the progress bar advances smoothly while a large campaign sends.
const BATCH_SIZE = 5000;
// Staging requests are independent, so a few run at once. Kept small deliberately: this
// multiplies load on the gateway and the database pool behind it.
const STAGE_CONCURRENCY = 4;
const HARD_MAX = 2_000_000;

interface Progress {
  sent: number;
  total: number;
  phase: string;
}

interface BulkJobStatus {
  bulkJobId: number;
  campaignId: number | null;
  campaignName: string | null;
  status: string;
  totalRecipients: number;
  enqueuedCount: number;
  failedReason: string | null;
}

const JOB_TERMINAL = ["COMPLETED", "PARTIAL", "FAILED"];

export default function BulkSmsPage() {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [sourceAddr, setSourceAddr] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [manualRaw, setManualRaw] = useState("");
  const [fileNumbers, setFileNumbers] = useState<string[]>([]);
  const [fileName, setFileName] = useState("");
  const [message, setMessage] = useState("");
  const [flash, setFlash] = useState(false);

  const [schedule, setSchedule] = useState(false);
  const [scheduleLocal, setScheduleLocal] = useState("");

  const [error, setError] = useState("");
  const [result, setResult] = useState<BulkResult | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingScheduleAt, setPendingScheduleAt] = useState<string | undefined>(undefined);
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobStatus, setJobStatus] = useState<BulkJobStatus | null>(null);

  // Once a job is started, poll its background-ingestion progress. This is purely
  // informational — the gateway keeps charging + enqueueing even if this page is
  // closed, so leaving mid-send is safe.
  useEffect(() => {
    if (!jobId) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await api<BulkJobStatus>(`sms/bulk/jobs/status?bulkJobId=${jobId}`);
        if (!active) return;
        setJobStatus(s);
        notifyAccountChanged(); // credit is drained as the job enqueues — refresh the sidebar
        if (JOB_TERMINAL.includes(s.status)) return; // stop polling
      } catch {
        // transient error — keep polling
      }
      if (active) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [jobId]);

  // Fill the recipient list from a saved group OR a segment — both resolve to contacts.
  const loadRecipients = useCallback((contacts: Contact[], sourceName: string) => {
    setFileNumbers([]);
    setFileName("");
    setManualRaw(contacts.map((c) => c.msisdn).join("\n"));
    setCampaignName((n) => n || sourceName);
    toast.success(`Loaded ${contacts.length.toLocaleString()} contact(s) from "${sourceName}".`);
  }, [toast]);

  // Adapter for GroupPicker, which hands back the group object.
  const loadGroup = useCallback(
    (contacts: Contact[], group: ContactGroup) => loadRecipients(contacts, group.name),
    [loadRecipients]
  );

  // Deep link from Contacts → "Send to group" (?groupId=123). Read from location rather than
  // useSearchParams so the page needs no Suspense boundary.
  //
  // Guarded so it can only ever run ONCE: loading the group shows a toast, and a toast re-renders
  // the toast provider — without this guard that feedback loop re-triggers the effect forever.
  const deepLinkLoaded = useRef(false);
  useEffect(() => {
    if (deepLinkLoaded.current) return;
    const params = new URLSearchParams(window.location.search);
    const groupId = params.get("groupId");
    const segmentId = params.get("segmentId");
    if (!groupId && !segmentId) return;
    deepLinkLoaded.current = true;

    // A segment is re-evaluated right now, so the recipient list is whoever matches TODAY.
    if (segmentId) {
      api<{ recipients: Contact[] }>(`account/segments/recipients?segmentId=${segmentId}`)
        .then(async (r) => {
          const list = r.recipients || [];
          if (list.length === 0) return;
          const segs = await api<{ segments: ContactSegment[] }>("account/segments").catch(() => ({ segments: [] }));
          const s = segs.segments.find((x) => String(x.segmentId) === segmentId);
          loadRecipients(list, s?.name ?? "segment");
        })
        .catch(() => { /* a bad/stale link just leaves the form empty */ });
      return;
    }

    api<{ recipients: Contact[] }>(`account/contact-groups/recipients?groupId=${groupId}`)
      .then(async (r) => {
        const list = r.recipients || [];
        if (list.length === 0) return;
        const groups = await api<{ groups: ContactGroup[] }>("account/contact-groups").catch(() => ({ groups: [] }));
        const g = groups.groups.find((x) => String(x.groupId) === groupId);
        loadRecipients(list, g?.name ?? "group");
      })
      .catch(() => { /* a bad/stale link just leaves the form empty */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a file is loaded it is the recipient source; otherwise use the textarea.
  const manualNumbers = useMemo(() => (fileNumbers.length ? [] : parseNumbers(manualRaw)), [manualRaw, fileNumbers]);
  const recipients = fileNumbers.length ? fileNumbers : manualNumbers;
  const count = recipients.length;

  const est = useMemo(() => estimateSegments(message), [message]);
  const batches = Math.ceil(count / BATCH_SIZE);

  // Per-category credit rates so the estimate reflects local/national/international
  // pricing instead of assuming 1 credit/segment.
  const { data: ratesData } = useFetch<{ rates: SmsRate[] }>("sms/rates");
  const { data: accountData } = useFetch<Account>("account");
  const lookup = useMemo(() => (ratesData ? buildRateLookup(ratesData.rates) : null), [ratesData]);

  // Count recipients per category once (independent of message length) so changing
  // the message only re-multiplies a small summary, not the whole recipient list.
  // Categorising every recipient is a full pass over the list, and it runs at least twice —
  // once when the file is parsed and again when the rate table arrives. Done synchronously in
  // a useMemo that is a multi-second frozen tab on a million-plus list (no scrolling, no
  // typing, no cancel). Instead it is walked in slices that yield to the browser between each,
  // so the page stays responsive while the estimate fills in.
  const [breakdown, setBreakdown] = useState<{ counts: Record<string, number>; rate: Record<string, number> } | null>(null);
  const [breakdownPending, setBreakdownPending] = useState(false);

  useEffect(() => {
    if (!lookup) {
      setBreakdown(null);
      setBreakdownPending(false);
      return;
    }

    if (recipients.length === 0) {
      setBreakdown({ counts: {}, rate: {} });
      setBreakdownPending(false);
      return;
    }

    let cancelled = false;
    const counts: Record<string, number> = {};
    const rate: Record<string, number> = {};
    let index = 0;
    const SLICE = 25000;

    setBreakdownPending(true);

    const step = () => {
      if (cancelled) return;
      const end = Math.min(index + SLICE, recipients.length);
      for (; index < end; index += 1) {
        const { category, creditsPerSms } = categorize(lookup, recipients[index]);
        counts[category] = (counts[category] ?? 0) + 1;
        rate[category] = creditsPerSms;
      }
      if (index < recipients.length) {
        setTimeout(step, 0); // hand the main thread back before the next slice
        return;
      }
      setBreakdown({ counts, rate });
      setBreakdownPending(false);
    };

    step();
    // A new file (or new rates) supersedes an in-flight pass.
    return () => {
      cancelled = true;
    };
  }, [recipients, lookup]);

  // Total credits = segments/msg × Σ (recipients in category × category rate).
  // Falls back to the segment-only estimate until rates load.
  const totalCredits = useMemo(() => {
    if (!breakdown) return est.segments * count;
    let perMsg = 0;
    for (const cat of Object.keys(breakdown.counts)) perMsg += breakdown.counts[cat] * (breakdown.rate[cat] ?? 1);
    return est.segments * perMsg;
  }, [breakdown, est.segments, count]);

  const intlCount = breakdown?.counts.INTERNATIONAL ?? 0;
  const blockedIntl = intlCount > 0 && accountData?.allowInternational === false;

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseNumbers(String(reader.result ?? ""));
      if (parsed.length === 0) {
        setError("No phone numbers found in the file.");
        return;
      }
      setFileNumbers(parsed);
      setFileName(file.name);
      toast.success(`Loaded ${parsed.length.toLocaleString()} numbers from ${file.name}.`);
    };
    reader.onerror = () => setError("Could not read the file.");
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = "";
  }

  function clearFile() {
    setFileNumbers([]);
    setFileName("");
  }

  function resolveScheduleAt(): string | undefined {
    if (!schedule) return undefined;
    if (!scheduleLocal) throw new Error("Pick a date and time to schedule.");
    const when = new Date(scheduleLocal);
    if (Number.isNaN(when.getTime())) throw new Error("Invalid schedule date.");
    if (when.getTime() <= Date.now()) throw new Error("Schedule time must be in the future.");
    return when.toISOString();
  }

  // Validate, then open the confirmation modal (don't send yet).
  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setResult(null);

    if (count === 0) {
      setError("Add at least one recipient number.");
      return;
    }
    if (count > HARD_MAX) {
      setError(`Too many recipients (${count.toLocaleString()}). The maximum is ${HARD_MAX.toLocaleString()}.`);
      return;
    }
    if (blockedIntl) {
      setError(`${intlCount.toLocaleString()} recipient(s) are international, which your account is not permitted to send. Remove them or contact support to enable international SMS.`);
      return;
    }

    try {
      setPendingScheduleAt(resolveScheduleAt());
    } catch (err) {
      setError((err as Error).message);
      return;
    }

    setConfirmOpen(true);
  }

  // Fire-and-forget: stage the whole list server-side, then start a background job
  // that charges + enqueues it. The browser only has to finish the (fast) staging
  // uploads; after that the campaign sends on its own and you can leave the page.
  async function doSend() {
    setConfirmOpen(false);
    const scheduleAt = pendingScheduleAt;
    const all = recipients;
    const total = all.length;

    setSending(true);
    setResult(null);
    setJobId(null);
    setJobStatus(null);
    setProgress({ sent: 0, total, phase: "Staging recipients" });

    try {
      // 1) Create the job (captures sender, message, schedule, flash).
      const created = await api<{ bulkJobId: number }>("sms/bulk/jobs", {
        method: "POST",
        body: {
          sourceAddr,
          campaignName: campaignName.trim() || undefined,
          message,
          flash,
          scheduleAt
        }
      });
      const newJobId = created.bulkJobId;

      // 2) Upload recipients in chunks — fast staging inserts, no charge yet.
      // Uploaded with bounded concurrency instead of one-at-a-time: a 2M recipient list was
      // 1,000 strictly sequential round trips, which is minutes of waiting on this page.
      await stageInBatches({
        items: all,
        batchSize: BATCH_SIZE,
        concurrency: STAGE_CONCURRENCY,
        upload: (chunk) =>
          api("sms/bulk/jobs/recipients", {
            method: "POST",
            body: { bulkJobId: newJobId, destinations: chunk }
          }),
        onProgress: (n) => setProgress({ sent: n, total, phase: "Staging recipients" })
      });

      // 3) Start background ingestion (charge + enqueue happens on the server).
      await api("sms/bulk/jobs/start", { method: "POST", body: { bulkJobId: newJobId } });

      setResult({ campaignId: null, accepted: total, status: scheduleAt ? "SCHEDULED" : "PROCESSING", scheduledAt: scheduleAt });
      setJobId(newJobId); // begins progress polling
      toast.success(`${total.toLocaleString()} recipient(s) staged — sending in the background. You can leave this page.`);

      // Clear inputs for the next campaign (keep the selected sender).
      setManualRaw("");
      clearFile();
      setMessage("");
      setFlash(false);
      setCampaignName("");
      setSchedule(false);
      setScheduleLocal("");
      setPendingScheduleAt(undefined);
      setProgress(null);
    } catch (err) {
      setError((err as Error).message);
      toast.error((err as Error).message);
      setProgress(null);
    } finally {
      setSending(false);
    }
  }

  const pct = progress && progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;
  const jobPct = jobStatus && jobStatus.totalRecipients > 0
    ? Math.round((jobStatus.enqueuedCount / jobStatus.totalRecipients) * 100)
    : 0;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Bulk SMS" subtitle="Send one message to many recipients. Paste numbers or upload a large list." />

      <form onSubmit={onSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
        <Card>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SenderSelect value={sourceAddr} onChange={setSourceAddr} required />
              <Input
                label="Campaign name (optional)"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="June promo"
              />
            </div>

            <div>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700">Recipients</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">
                    {count.toLocaleString()} number{count === 1 ? "" : "s"}
                    {batches > 1 && ` · ${batches} batches`}
                  </span>
                  <GroupPicker onPick={loadGroup} />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="text-xs font-medium text-brand-600 hover:underline"
                  >
                    Upload .txt / .csv
                  </button>
                </div>
              </div>

              {fileNumbers.length > 0 ? (
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{fileName}</p>
                    <p className="text-xs text-gray-500">{fileNumbers.length.toLocaleString()} recipients loaded</p>
                  </div>
                  <button type="button" onClick={clearFile} className="text-xs font-medium text-red-600 hover:underline">
                    Remove
                  </button>
                </div>
              ) : (
                <Textarea
                  rows={7}
                  value={manualRaw}
                  onChange={(e) => setManualRaw(e.target.value)}
                  placeholder={"One number per line, or comma/space separated.\n2526xxxxxxx\n2526yyyyyyy"}
                  hint="Separators (newline, comma, semicolon, space) accepted. Duplicates removed. For very large lists, upload a file."
                />
              )}
              <input ref={fileRef} type="file" accept=".txt,.csv,text/plain,text/csv" onChange={onFile} className="hidden" />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Message</span>
                <TemplatePicker onPick={setMessage} />
              </div>
              <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <Badge>{est.encoding}</Badge>
              <span>{est.chars} chars</span>
              <span>·</span>
              <span>{est.segments} segment{est.segments === 1 ? "" : "s"}/msg</span>
              <span>·</span>
              <span className="font-medium text-gray-700">~{totalCredits.toLocaleString()} credits total</span>
              {breakdown &&
                CAT_ORDER.filter((c) => breakdown.counts[c]).map((c) => (
                  <Badge key={c} tone={CAT_TONE[c]} dot>
                    {CAT_LABEL[c]} {breakdown.counts[c].toLocaleString()} × {breakdown.rate[c]}
                  </Badge>
                ))}
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={flash} onChange={(e) => setFlash(e.target.checked)} />
              Flash SMS <span className="text-xs text-gray-400">(shows on screen, not saved)</span>
            </label>

            {/* Scheduling */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="radio" checked={!schedule} onChange={() => setSchedule(false)} />
                  Send now
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="radio" checked={schedule} onChange={() => setSchedule(true)} />
                  Schedule for later
                </label>
                {schedule && (
                  <input
                    type="datetime-local"
                    value={scheduleLocal}
                    min={datetimeLocalMin()}
                    onChange={(e) => setScheduleLocal(e.target.value)}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
                  />
                )}
              </div>
              {schedule && scheduleLocal && (
                <p className="mt-2 text-xs text-gray-500">
                  Will be queued to send at {new Date(scheduleLocal).toLocaleString()} (your local time).
                </p>
              )}
            </div>

            {progress && sending && (
              <div>
                <div className="mb-1 flex justify-between text-xs text-gray-500">
                  <span>{progress.phase}…</span>
                  <span>
                    {progress.sent.toLocaleString()} / {progress.total.toLocaleString()} ({pct}%)
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}

            {blockedIntl && (
              <Alert>
                {intlCount.toLocaleString()} recipient(s) are international, which your account is not permitted to send. Remove them or contact support to enable international SMS.
              </Alert>
            )}
            <Alert>{error}</Alert>
            <Button type="submit" size="lg" disabled={sending || !sourceAddr || count === 0 || blockedIntl || breakdownPending}>
              {sending ? "Sending…" : schedule ? "Review & Schedule" : "Review & Send"}
            </Button>
          </div>
        </Card>
          </div>

          {/* Campaign summary */}
          <div>
            <Card title="Campaign Summary" className="lg:sticky lg:top-6">
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Recipients</dt>
                  <dd className="text-lg font-bold tabular-nums text-gray-900">{count.toLocaleString()}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Batches</dt>
                  <dd className="font-medium tabular-nums text-gray-900">{batches} × {BATCH_SIZE.toLocaleString()}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Encoding</dt>
                  <dd><Badge tone="blue">{est.encoding}</Badge></dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Segments / msg</dt>
                  <dd className="font-medium tabular-nums text-gray-900">{est.segments}</dd>
                </div>
                {breakdown && CAT_ORDER.some((c) => breakdown.counts[c]) && (
                  <div className="space-y-1.5 border-t border-gray-100 pt-3">
                    {CAT_ORDER.filter((c) => breakdown.counts[c]).map((c) => (
                      <div key={c} className="flex items-center justify-between">
                        <dt className="flex items-center gap-1.5 text-gray-500">
                          <Badge tone={CAT_TONE[c]} dot>{CAT_LABEL[c]}</Badge>
                          <span className="text-xs">{breakdown.counts[c].toLocaleString()} × {breakdown.rate[c]}</span>
                        </dt>
                        <dd className="font-medium tabular-nums text-gray-900">
                          {(breakdown.counts[c] * breakdown.rate[c] * est.segments).toLocaleString()}
                        </dd>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                  <dt className="text-gray-500">Est. total credits</dt>
                  <dd className="text-lg font-bold tabular-nums text-brand-600">~{totalCredits.toLocaleString()}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">When</dt>
                  <dd className="text-right font-medium text-gray-900">
                    {schedule ? (scheduleLocal ? new Date(scheduleLocal).toLocaleString() : "Scheduled") : "Send now"}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>
        </div>
      </form>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={pendingScheduleAt ? "Confirm scheduled campaign" : "Confirm bulk send"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={doSend}>{pendingScheduleAt ? "Confirm & Schedule" : "Confirm & Send"}</Button>
          </>
        }
      >
        <SenderHeader sender={sourceAddr} />
        <dl>
          <ConfirmRow label="Recipients">{count.toLocaleString()}</ConfirmRow>
          {campaignName.trim() && <ConfirmRow label="Campaign">{campaignName.trim()}</ConfirmRow>}
          <ConfirmRow label="Batches">
            {batches} × up to {BATCH_SIZE.toLocaleString()}
          </ConfirmRow>
          <ConfirmRow label="Type">{flash ? "Flash (class 0)" : "Standard"}</ConfirmRow>
          <ConfirmRow label="Segments / msg">
            {est.segments} ({est.encoding})
          </ConfirmRow>
          {breakdown && CAT_ORDER.some((c) => breakdown.counts[c]) && (
            <ConfirmRow label="By category">
              {CAT_ORDER.filter((c) => breakdown.counts[c])
                .map((c) => `${CAT_LABEL[c]} ${breakdown.counts[c].toLocaleString()}×${breakdown.rate[c]}`)
                .join(" · ")}
            </ConfirmRow>
          )}
          <ConfirmRow label="Est. credits">~{totalCredits.toLocaleString()}</ConfirmRow>
          <ConfirmRow label="When">
            {pendingScheduleAt ? new Date(pendingScheduleAt).toLocaleString() : "Send now"}
          </ConfirmRow>
        </dl>
        <MessagePreview message={message} />
        <p className="mt-3 text-xs text-gray-500">
          Confirm the sender header is correct — it appears as the “from” on every recipient’s phone.
        </p>
      </Modal>

      {result && (
        <div className="mt-6">
          <Card title={result.scheduledAt ? "Campaign scheduled" : "Campaign started"}>
            <p className="mb-4 text-sm text-gray-500">
              {(result.accepted ?? 0).toLocaleString()} recipient(s) staged. The gateway is charging and
              queuing them in the background — you can leave this page; the send continues.
            </p>

            {jobStatus && (
              <div className="mb-4">
                <div className="mb-1 flex justify-between text-xs text-gray-500">
                  <span>
                    {JOB_TERMINAL.includes(jobStatus.status) ? "Enqueued" : "Enqueuing"} to send queue
                  </span>
                  <span>
                    {jobStatus.enqueuedCount.toLocaleString()} / {jobStatus.totalRecipients.toLocaleString()} ({jobPct}%)
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full rounded-full transition-all ${jobStatus.status === "FAILED" ? "bg-red-500" : "bg-brand-500"}`}
                    style={{ width: `${jobPct}%` }}
                  />
                </div>
              </div>
            )}

            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-gray-500">Status</dt>
              <dd>
                <Badge tone={jobStatus?.status === "FAILED" ? "red" : jobStatus?.status === "COMPLETED" ? "green" : "blue"}>
                  {String(jobStatus?.status ?? result.status ?? "PROCESSING")}
                </Badge>
              </dd>
              <dt className="text-gray-500">Campaign</dt>
              <dd className="font-medium">{jobStatus?.campaignId ? `#${jobStatus.campaignId}` : "pending…"}</dd>
              <dt className="text-gray-500">Recipients</dt>
              <dd className="font-medium tabular-nums">{(result.accepted ?? 0).toLocaleString()}</dd>
              {result.scheduledAt && (
                <>
                  <dt className="text-gray-500">Scheduled for</dt>
                  <dd>{new Date(result.scheduledAt).toLocaleString()}</dd>
                </>
              )}
            </dl>

            {jobStatus?.failedReason && (
              <p className="mt-3 text-xs text-red-600">
                Stopped early: {jobStatus.failedReason}
              </p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
